/**
 * One outstanding ACTION inside a case card — the redesign's row
 * grammar, now that the card carries the case identity:
 *
 *   [new-dot] (kind chip) what needs doing · £1,500 · TAT 5d   [due] [assignee] [age]
 *             latest message preview…
 *
 * Clicking (or Enter/Space — rows are real buttons for a11y) SELECTS
 * the row into the preview pane; navigation lives on the pane's
 * primary action. The avatar on the rail only appears for a per-item
 * assignee OVERRIDE (the case owner sits on the card header).
 */

import { Badge, Group, Menu, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';

import { useAnalystRoster } from '@/hooks';
import { InboxAnalyst, InboxItem } from '@/types/inbox';
import {
  analystInitials,
  commercialsLine,
  dueInfo,
  kindChip,
  staleNote,
  waitingNote,
} from './queueFormat';
import { timeAgo } from './timeAgo';

/** Case-ownership context for the card's avatar dropdown. */
export interface RowOwnerContext {
  familyKey: string;
  /** The CASE owner (distinct from the effective avatar, which may be
   * an item-level override). */
  caseAnalyst: InboxAnalyst | null;
  currentUserId: string | null;
  onClaim: (familyKey: string, userId: string | null) => Promise<void>;
}

const CHIP_COLOR: Record<string, string> = {
  gray: 'gray',
  blue: 'blue',
  teal: 'teal',
  violet: 'violet',
  orange: 'orange',
  red: 'red',
};

/**
 * The row's one-line label. Emitters bake the codename into titles
 * ("Client message: F001-32", "Ready to Start: F001-32-1") — inside a
 * card the codename already heads the group, so a trailing codename
 * suffix is dropped. Waiting rows say who holds the ball instead.
 */
export function rowTitle(item: InboxItem, familyKey: string | null): string {
  const waiting = waitingNote(item);
  if (waiting) {
    return waiting;
  }
  const title = item.title.trim();
  const codenames = [item.codenameCached, familyKey].filter(
    (c): c is string => typeof c === 'string' && c.length > 0
  );
  for (const codename of codenames) {
    for (const sep of [': ', ' — ', ' - ']) {
      const suffix = `${sep}${codename}`;
      if (title.endsWith(suffix) && title.length > suffix.length) {
        return title.slice(0, -suffix.length).trim();
      }
    }
  }
  return title;
}

export function DueBadge({ item }: Readonly<{ item: InboxItem }>) {
  const due = dueInfo(item);
  if (!due) {
    const stale = staleNote(item);
    return stale ? (
      <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }} data-testid="stale-note">
        {stale}
      </Text>
    ) : null;
  }
  if (due.tone === 'quiet') {
    return (
      <Tooltip label={`Due ${due.exact}`} withArrow>
        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }} data-testid="due-badge">
          {due.label}
        </Text>
      </Tooltip>
    );
  }
  return (
    <Tooltip label={`Due ${due.exact}`} withArrow>
      <Badge
        size="sm"
        variant="light"
        color={due.tone === 'overdue' ? 'red' : 'orange'}
        style={{ textTransform: 'none' }}
        data-testid="due-badge"
      >
        {due.label}
      </Badge>
    </Tooltip>
  );
}

export function AnalystAvatar({
  analyst,
  size = 22,
  emptyLabel = 'Unclaimed',
}: Readonly<{ analyst: InboxAnalyst | null; size?: number; emptyLabel?: string }>) {
  if (!analyst) {
    return (
      <Tooltip label={emptyLabel} withArrow>
        <Text
          component="span"
          data-testid="analyst-avatar-unclaimed"
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            border: '1.5px dashed var(--mantine-color-gray-5)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--mantine-color-gray-5)',
            fontSize: 10,
            flex: 'none',
          }}
        >
          ·
        </Text>
      </Tooltip>
    );
  }
  return (
    <Tooltip label={analyst.name ?? analyst.userId} withArrow>
      <Text
        component="span"
        data-testid="analyst-avatar"
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: 'var(--mantine-color-navy-7, var(--mantine-color-dark-6))',
          color: 'var(--mantine-color-white)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.02em',
          flex: 'none',
        }}
      >
        {analystInitials(analyst.name, analyst.userId)}
      </Text>
    </Tooltip>
  );
}

/** Roster items rendered INSIDE the opened dropdown so the roster fetch
 * only happens when someone actually opens a menu (and the analyst
 * roster is cached module-wide after the first open). */
function OwnerMenuItems({ owner }: Readonly<{ owner: RowOwnerContext }>) {
  const members = useAnalystRoster();
  const caseOwnerId = owner.caseAnalyst?.userId ?? null;
  return (
    <>
      <Menu.Label>{caseOwnerId ? 'Reassign case to…' : 'Claim / assign case…'}</Menu.Label>
      {owner.currentUserId && caseOwnerId !== owner.currentUserId && (
        <Menu.Item onClick={() => void owner.onClaim(owner.familyKey, owner.currentUserId)}>
          {caseOwnerId ? 'Claim for me' : 'Claim case'}
        </Menu.Item>
      )}
      {members
        .filter((m) => String(m.id) !== caseOwnerId)
        .map((m) => (
          <Menu.Item key={m.id} onClick={() => void owner.onClaim(owner.familyKey, String(m.id))}>
            {m.name}
          </Menu.Item>
        ))}
      {caseOwnerId && (
        <>
          <Menu.Divider />
          <Menu.Item color="red" onClick={() => void owner.onClaim(owner.familyKey, null)}>
            Release case
          </Menu.Item>
        </>
      )}
    </>
  );
}

/** The case-owner avatar as a claim/assign dropdown. Clicks must not
 * select the row / card underneath. Shared with the Dashboard tab's
 * analyst column — same roster, same family-wide claim semantics. */
export function OwnerAvatarMenu({
  analyst,
  owner,
}: Readonly<{ analyst: InboxAnalyst | null; owner: RowOwnerContext }>) {
  return (
    <Menu withinPortal position="bottom-end">
      <Menu.Target>
        <UnstyledButton
          onClick={(e) => e.stopPropagation()}
          aria-label="Claim or assign this case"
          data-testid="row-owner-menu"
          style={{ display: 'inline-flex', flex: 'none' }}
        >
          <AnalystAvatar analyst={analyst} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
        <OwnerMenuItems owner={owner} />
      </Menu.Dropdown>
    </Menu>
  );
}

interface QueueRowProps {
  item: InboxItem;
  /** The card's family key — used to trim the codename echo out of
   * the item title. */
  familyKey: string | null;
  selected: boolean;
  /** Unseen-by-anyone marker (last_seen_at null and not locally seen). */
  isNew: boolean;
  onSelect: (item: InboxItem) => void;
}

export function QueueRow({ item, familyKey, selected, isNew, onSelect }: Readonly<QueueRowProps>) {
  const chip = kindChip(item.kind);
  const commercials = commercialsLine(item);
  const label = rowTitle(item, familyKey);
  const preview = item.body?.trim() ? item.body.trim() : null;
  const snoozed = item.state === 'snoozed';

  return (
    <UnstyledButton
      // A div-with-button-semantics: the rail can host a REAL button
      // (menus) and nested <button>s are invalid HTML.
      component="div"
      role="button"
      tabIndex={0}
      onClick={() => onSelect(item)}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onSelect(item);
        }
      }}
      aria-current={selected ? 'true' : undefined}
      data-testid="queue-row"
      data-selected={selected || undefined}
      px="sm"
      py={6}
      style={{
        display: 'grid',
        gridTemplateColumns: '12px minmax(0, 1fr) auto',
        columnGap: 8,
        alignItems: 'center',
        width: '100%',
        cursor: 'pointer',
        borderLeft: selected ? '3px solid var(--mantine-color-yellow-5)' : '3px solid transparent',
        background: selected ? 'var(--mantine-color-yellow-0)' : undefined,
        opacity: snoozed ? 0.7 : 1,
      }}
    >
      {isNew ? (
        <Text
          component="span"
          data-testid="queue-row-new"
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: 'var(--mantine-color-yellow-5)',
            justifySelf: 'center',
          }}
        />
      ) : (
        <span />
      )}
      <Stack gap={2} style={{ minWidth: 0 }}>
        <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
          <Badge
            size="xs"
            variant="light"
            color={CHIP_COLOR[chip.tone]}
            style={{ textTransform: 'none', flex: 'none' }}
          >
            {chip.label}
          </Badge>
          {item.priority === 'high' && chip.tone !== 'red' && (
            <Badge size="xs" variant="light" color="red" style={{ flex: 'none' }}>
              High
            </Badge>
          )}
          <Text size="sm" truncate="end" style={{ minWidth: 0 }} data-testid="queue-row-title">
            {label}
          </Text>
          {commercials && (
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap', flex: 'none' }}>
              · {commercials}
            </Text>
          )}
        </Group>
        {preview && (
          <Text size="xs" c="dimmed" truncate="end" data-testid="queue-row-preview">
            {preview}
          </Text>
        )}
      </Stack>
      <Group gap={8} wrap="nowrap" justify="flex-end">
        {snoozed && item.wakeAt ? (
          <Text size="xs" c="orange" style={{ whiteSpace: 'nowrap' }}>
            wakes {timeAgo(item.wakeAt, new Date(Date.now() + 1))}
          </Text>
        ) : (
          <DueBadge item={item} />
        )}
        {item.assignee && (
          <AnalystAvatar analyst={item.assignee} size={18} emptyLabel="Follows case analyst" />
        )}
        <Text size="xs" c="dimmed" style={{ minWidth: 30, textAlign: 'right' }}>
          {timeAgo(item.updatedAt ?? item.createdAt)}
        </Text>
      </Group>
    </UnstyledButton>
  );
}
